import React, {useContext, useMemo, useState} from 'react';
import {Pressable, ScrollView, StyleSheet, TouchableOpacity, View, ViewStyle} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {BottomTabBarHeightContext} from '@react-navigation/bottom-tabs';

import * as GiftCardStoreCardStories from '../components/GiftCardStoreCard.stories';
import * as CreditCardVisualStories from '../components/payments/till/PaymentMethod/CreditCardVisual.stories';
import * as PaymentMethodSelectorStories from '../components/payments/till/PaymentMethodSelector/PaymentMethodSelector.stories';
import * as ProductTileStories from '../components/ProductTile/ProductTile.stories';
import * as AddToWalletStories from '../components/rewards/AddToWallet/AddToWallet.stories';
import * as ExclusiveOffersCarouselStories from '../components/rewards/ExclusiveOffers/ExclusiveOffers.stories';
import * as ExclusiveOffersTileCompactStories from '../components/rewards/ExclusiveOffers/ExclusiveOffersTileCompact.stories';
import * as ExclusiveOffersTileLargeStories from '../components/rewards/ExclusiveOffers/ExclusiveOffersTileLarge.stories';
import * as InStoreCodeStories from '../components/rewards/InStoreCode/InStoreCode.stories';
import * as MemberCardStories from '../components/rewards/MemberCard/MemberCard.stories';
import * as SkeletonCompositionsStories from '../components/skeleton/SkeletonCompositions.stories';
import * as SkeletonPrimitivesStories from '../components/skeleton/SkeletonPrimitives.stories';
import * as BrandThemeStories from '../components/themed/BrandThemeShowcase.stories';
import * as ButtonStories from '../components/themed/Button.stories';
import Text from '../components/themed/Text';
import * as TypographyStories from '../components/themed/Text.stories';
import * as TextInputStories from '../components/themed/TextInput.stories';
import * as OtpStories from '../components/ui-controls/form/Otp.stories';
import ContentView from '../components/ui-controls/layout/ContentView';
import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import {AppRoute} from '../models/AppRoute';
import {RouteNavigatorScreenProps} from '../models/navigationTypes';
import theme from '../style/theme';

import * as AccountCreateScreenStories from './account/AccountCreateScreen/AccountCreateScreenLoyaltyEnabled.stories';
import * as AccountRewardsScreenStories from './account/AccountRewardsScreen/AccountRewardsScreen.stories';
import * as GiftCardPromotionExtraContentStories from './articles/GiftCardPromotionExtraContent.stories';
import * as ForceUpgradeScreenStories from './ForceUpgradeScreen/ForceUpgradeScreenContent.stories';

/**
 * Storybook Screen Wrapper (Web Version)
 *
 * Professional two-column Storybook browser for web development builds.
 * Responsive: Desktop shows sidebar + content, Mobile shows drawer overlay.
 */

type StoryModule = {
  default: {
    title: string;
    component?: React.ComponentType<any>;
    decorators?: Array<(Story: React.ComponentType) => JSX.Element>;
  };
  [key: string]: any;
};

type StoryInfo = {
  title: string;
  storyName: string;
  fullName: string;
  module: StoryModule;
  story: any;
};

type ComponentGroup = {
  category: string;
  componentName: string;
  stories: StoryInfo[];
};

const storyModules: StoryModule[] = [
  TypographyStories as any,
  ButtonStories as any,
  TextInputStories as any,
  OtpStories as any,
  SkeletonPrimitivesStories as any,
  SkeletonCompositionsStories as any,
  BrandThemeStories as any,
  MemberCardStories as any,
  InStoreCodeStories as any,
  AddToWalletStories as any,
  ExclusiveOffersTileLargeStories as any,
  ExclusiveOffersTileCompactStories as any,
  ExclusiveOffersCarouselStories as any,
  ProductTileStories as any,
  CreditCardVisualStories as any,
  PaymentMethodSelectorStories as any,
  AccountCreateScreenStories as any,
  AccountRewardsScreenStories as any,
  ForceUpgradeScreenStories as any,
  GiftCardStoreCardStories as any,
  GiftCardPromotionExtraContentStories as any,
];

const ChevronIcon = ({expanded}: {expanded: boolean}) => (
  <Text style={[styles.chevron, expanded && styles.chevronExpanded]}>›</Text>
);

const StorybookScreen = (_props: RouteNavigatorScreenProps<AppRoute.STORYBOOK>) => {
  const {isWebDesktopScreen} = useWebScreenDimensions();
  // BottomTabBarHeightContext returns null when not inside a BottomTabNavigator
  // (e.g. desktop web Stack). Default to 0.
  const tabBarHeight = useContext(BottomTabBarHeightContext) ?? 0;
  const insets = useSafeAreaInsets();
  // Bottom padding to clear native/web mobile tab bar; falls back to safe-area
  // inset on desktop. Applied to ScrollView contentContainers below.
  const bottomPadding = tabBarHeight > 0 ? tabBarHeight : insets.bottom;
  const [selectedStory, setSelectedStory] = useState<StoryInfo | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showInfo, setShowInfo] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Group stories by component
  const groupedStories = useMemo(() => {
    const groups: Map<string, ComponentGroup> = new Map();

    storyModules.forEach(module => {
      const meta = module.default;
      const fullTitle = meta?.title || 'Unknown';

      const parts = fullTitle.split('/');
      const componentName = parts[parts.length - 1];
      const category = parts.length > 2 ? parts[1] : parts.length > 1 ? parts[0] : 'General';

      const stories = Object.keys(module)
        .filter(key => key !== 'default')
        .map(storyName => ({
          title: fullTitle,
          storyName,
          fullName: `${fullTitle} / ${storyName}`,
          module,
          story: module[storyName],
        }));

      const groupKey = fullTitle;
      groups.set(groupKey, {
        category,
        componentName,
        stories,
      });
    });

    const byCategory: Map<string, ComponentGroup[]> = new Map();
    groups.forEach(group => {
      const existing = byCategory.get(group.category) || [];
      existing.push(group);
      byCategory.set(group.category, existing);
    });

    return byCategory;
  }, []);

  const totalStories = useMemo(() => {
    let count = 0;
    groupedStories.forEach(groups => {
      groups.forEach(g => {
        count += g.stories.length;
      });
    });
    return count;
  }, [groupedStories]);

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  };

  const handleSelectStory = (story: StoryInfo) => {
    setSelectedStory(story);
    if (!isWebDesktopScreen) {
      setSidebarOpen(false);
    }
  };

  const renderStoryContent = () => {
    if (!selectedStory) {
      return (
        <View style={styles.emptyState}>
          <Text style={styles.emptyStateIcon}>📚</Text>
          <Text variant="heading2" style={styles.emptyStateTitle}>
            Select a Story
          </Text>
          <Text variant="body" style={styles.emptyStateText}>
            {isWebDesktopScreen
              ? 'Choose a component from the sidebar to preview'
              : 'Tap the menu button to browse components'}
          </Text>
        </View>
      );
    }

    const {module, story} = selectedStory;
    const meta = module.default;

    let content: React.ReactNode;

    try {
      const StoryComponent = () => {
        if (typeof story.render === 'function') {
          return story.render();
        } else if (story.args && meta.component) {
          const Component = meta.component;
          return <Component {...story.args} />;
        } else {
          return (
            <View style={styles.errorContainer}>
              <Text>Story format not supported or missing component/render</Text>
            </View>
          );
        }
      };

      let FinalStory = StoryComponent;
      if (meta.decorators && Array.isArray(meta.decorators) && meta.decorators.length > 0) {
        const decoratorsCopy = [...meta.decorators].reverse();
        decoratorsCopy.forEach(decorator => {
          const CurrentStory = FinalStory;
          FinalStory = () => decorator(CurrentStory);
        });
      }

      content = <FinalStory />;
    } catch (error) {
      console.error('Error rendering story:', error);
      content = (
        <View style={styles.errorContainer}>
          <Text variant="heading3" style={styles.errorTitle}>
            Error Rendering Story
          </Text>
          <Text variant="body" style={styles.errorText}>
            {error instanceof Error ? error.message : String(error)}
          </Text>
        </View>
      );
    }

    return (
      <ScrollView
        style={styles.storyContent}
        contentContainerStyle={[styles.storyContentInner, {paddingBottom: bottomPadding + 20}]}>
        {content}
      </ScrollView>
    );
  };

  const renderSidebarContent = () => (
    <>
      <View style={styles.sidebarHeader}>
        <View style={styles.sidebarHeaderRow}>
          <View style={styles.sidebarTitleRow}>
            <Text variant="heading3" style={styles.sidebarTitle}>
              Storybook
            </Text>
            <View style={styles.sidebarCountChip}>
              <Text style={styles.sidebarCountText}>{totalStories}</Text>
            </View>
          </View>
          {!isWebDesktopScreen && (
            <TouchableOpacity style={styles.closeButton} onPress={() => setSidebarOpen(false)}>
              <Text style={styles.closeButtonText}>✕</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>

      <ScrollView
        style={styles.sidebarContent}
        contentContainerStyle={{paddingBottom: bottomPadding + 16}}>
        {Array.from(groupedStories.entries()).map(([category, groups]) => (
          <View key={category} style={styles.categorySection}>
            <Text style={styles.categoryTitle}>{category}</Text>

            {groups.map(group => {
              const groupKey = `${category}/${group.componentName}`;
              const isExpanded = expandedGroups.has(groupKey);

              return (
                <View key={groupKey} style={styles.componentGroup}>
                  <TouchableOpacity
                    style={styles.componentHeader}
                    onPress={() => toggleGroup(groupKey)}>
                    <ChevronIcon expanded={isExpanded} />
                    <Text style={styles.componentName}>{group.componentName}</Text>
                    <View style={styles.storyCount}>
                      <Text style={styles.storyCountText}>{group.stories.length}</Text>
                    </View>
                  </TouchableOpacity>

                  {isExpanded ? (
                    <View style={styles.storyList}>
                      {group.stories.map((story, idx) => {
                        const isSelected = selectedStory?.fullName === story.fullName;
                        return (
                          <TouchableOpacity
                            key={idx}
                            style={[styles.storyItem, isSelected && styles.storyItemSelected]}
                            onPress={() => handleSelectStory(story)}>
                            <Text
                              style={[
                                styles.storyItemText,
                                isSelected && styles.storyItemTextSelected,
                              ]}>
                              {story.storyName}
                            </Text>
                          </TouchableOpacity>
                        );
                      })}
                    </View>
                  ) : null}
                </View>
              );
            })}
          </View>
        ))}
      </ScrollView>
    </>
  );

  const renderDesktopLayout = () => (
    <View style={styles.container}>
      <View style={styles.sidebar}>{renderSidebarContent()}</View>
      <View style={styles.mainContent}>
        {/* Desktop Header */}
        <View style={styles.desktopHeader}>
          {selectedStory ? (
            <>
              <Text style={styles.headerBreadcrumb}>{selectedStory.module.default.title}</Text>
              <Text style={styles.headerSeparator}>/</Text>
              <Text style={styles.headerStoryName}>{selectedStory.storyName}</Text>
            </>
          ) : (
            <Text style={styles.headerStoryName}>Select a Story</Text>
          )}
        </View>
        {renderStoryContent()}
      </View>
    </View>
  );

  const renderMobileLayout = () => (
    <View style={styles.containerMobile}>
      {/* Mobile Header */}
      <View style={styles.mobileHeader}>
        <TouchableOpacity style={styles.menuButton} onPress={() => setSidebarOpen(true)}>
          <Text style={styles.menuButtonText}>☰</Text>
        </TouchableOpacity>
        <View style={styles.mobileHeaderContent}>
          {selectedStory ? (
            <>
              <Text style={styles.mobileHeaderBreadcrumb} numberOfLines={1}>
                {selectedStory.module.default.title}
              </Text>
              <Text style={styles.mobileHeaderTitle} numberOfLines={1}>
                {selectedStory.storyName}
              </Text>
            </>
          ) : (
            <Text style={styles.mobileHeaderTitle}>Storybook</Text>
          )}
        </View>
        <View style={styles.menuButtonPlaceholder} />
      </View>

      {/* Main Content */}
      <View style={styles.mainContent}>{renderStoryContent()}</View>

      {/* Sidebar Overlay */}
      {sidebarOpen ? (
        <>
          <Pressable style={styles.sidebarOverlay} onPress={() => setSidebarOpen(false)} />
          <View style={styles.sidebarMobile}>{renderSidebarContent()}</View>
        </>
      ) : null}
    </View>
  );

  return (
    <ContentView>
      {isWebDesktopScreen ? renderDesktopLayout() : renderMobileLayout()}

      {/* Floating Info Button — lifted above the bottom tab bar on mobile */}
      <TouchableOpacity
        style={[
          styles.floatingButton,
          !isWebDesktopScreen && styles.floatingButtonMobile,
          {bottom: (isWebDesktopScreen ? 24 : 16) + bottomPadding},
        ]}
        onPress={() => setShowInfo(!showInfo)}>
        <Text style={styles.floatingButtonText}>💡</Text>
      </TouchableOpacity>

      {/* Info Overlay */}
      {showInfo ? (
        <TouchableOpacity
          style={styles.overlay}
          activeOpacity={1}
          onPress={() => setShowInfo(false)}>
          <View style={styles.infoCard}>
            <Text variant="heading3" style={styles.infoTitle}>
              Full Storybook Experience
            </Text>
            <Text variant="body" style={styles.infoText}>
              For the complete Storybook with all features, controls, and addons:
            </Text>
            <View style={styles.codeBlock}>
              <Text style={styles.codeText}>npm run storybook:web</Text>
            </View>
            <Text variant="caption" style={styles.infoHint}>
              Opens at http://localhost:6006
            </Text>
          </View>
        </TouchableOpacity>
      ) : null}
    </ContentView>
  );
};

const SIDEBAR_WIDTH = 300;
const SIDEBAR_WIDTH_MOBILE = 280;
const PRIMARY_COLOR = theme.colour.general?.primary ?? '#0054A6';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: 'row',
    backgroundColor: '#f5f5f5',
  },
  containerMobile: {
    flex: 1,
    flexDirection: 'column',
    backgroundColor: '#f5f5f5',
  },

  // Sidebar styles (Light theme)
  sidebar: {
    width: SIDEBAR_WIDTH,
    backgroundColor: '#ffffff',
    borderRightWidth: 1,
    borderRightColor: '#e5e7eb',
  },
  sidebarMobile: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: SIDEBAR_WIDTH_MOBILE,
    backgroundColor: '#ffffff',
    zIndex: 100,
    shadowColor: '#000',
    shadowOffset: {width: 4, height: 0},
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 12,
  },
  sidebarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    zIndex: 99,
  },
  sidebarHeader: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    backgroundColor: '#ffffff',
  },
  sidebarHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sidebarTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  sidebarTitle: {
    color: '#111827',
    fontWeight: '600',
    fontSize: 14,
  },
  sidebarCountChip: {
    backgroundColor: '#f3f4f6',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  sidebarCountText: {
    color: '#6b7280',
    fontSize: 11,
    fontWeight: '600',
  },
  sidebarContent: {
    flex: 1,
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  closeButtonText: {
    fontSize: 16,
    color: '#6b7280',
  },

  // Desktop header
  desktopHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  headerBreadcrumb: {
    color: '#9ca3af',
    fontSize: 13,
  },
  headerSeparator: {
    color: '#d1d5db',
    fontSize: 13,
    marginHorizontal: 8,
  },
  headerStoryName: {
    color: '#111827',
    fontSize: 14,
    fontWeight: '600',
  },

  // Mobile header
  mobileHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
  },
  menuButton: {
    width: 36,
    height: 36,
    borderRadius: 8,
    backgroundColor: '#f3f4f6',
    justifyContent: 'center',
    alignItems: 'center',
  },
  menuButtonText: {
    fontSize: 18,
    color: '#374151',
  },
  menuButtonPlaceholder: {
    width: 36,
  },
  mobileHeaderContent: {
    flex: 1,
    marginHorizontal: 12,
  },
  mobileHeaderBreadcrumb: {
    color: '#9ca3af',
    fontSize: 11,
    textAlign: 'center',
  },
  mobileHeaderTitle: {
    color: '#111827',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },

  // Category styles
  categorySection: {
    paddingTop: 16,
  },
  categoryTitle: {
    color: '#9ca3af',
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 1,
    paddingHorizontal: 16,
    marginBottom: 8,
  },

  // Component group styles
  componentGroup: {
    marginBottom: 2,
  },
  componentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 16,
  } as ViewStyle,
  chevron: {
    color: '#9ca3af',
    fontSize: 14,
    width: 16,
    fontWeight: '600',
  },
  chevronExpanded: {
    transform: [{rotate: '90deg'}],
  },
  componentName: {
    color: '#374151',
    fontSize: 14,
    fontWeight: '500',
    flex: 1,
  },
  storyCount: {
    backgroundColor: '#f3f4f6',
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  storyCountText: {
    color: '#6b7280',
    fontSize: 11,
    fontWeight: '600',
  },

  // Story list styles
  storyList: {
    marginLeft: 32,
    borderLeftWidth: 1,
    borderLeftColor: '#e5e7eb',
  },
  storyItem: {
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderLeftWidth: 2,
    borderLeftColor: 'transparent',
    marginLeft: -1,
  } as ViewStyle,
  storyItemSelected: {
    backgroundColor: `${PRIMARY_COLOR}10`,
    borderLeftColor: PRIMARY_COLOR,
  },
  storyItemText: {
    color: '#6b7280',
    fontSize: 13,
  },
  storyItemTextSelected: {
    color: PRIMARY_COLOR,
    fontWeight: '600',
  },

  // Main content area
  mainContent: {
    flex: 1,
    backgroundColor: '#ffffff',
  },

  // Empty state
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  emptyStateIcon: {
    fontSize: 64,
    marginBottom: 24,
  },
  emptyStateTitle: {
    color: '#111827',
    marginBottom: 8,
  },
  emptyStateText: {
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 22,
  },

  // Story content — no horizontal padding so each story renders at the
  // full available width of the storybook content area (matches actual
  // app-rendered width). Stories that need framing/insets supply their
  // own decorator.
  storyContent: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  storyContentInner: {
    paddingHorizontal: 0,
    paddingTop: 0,
    minHeight: '100%',
  },

  // Error state
  errorContainer: {
    padding: 20,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  errorTitle: {
    color: '#dc2626',
    marginBottom: 8,
  },
  errorText: {
    color: '#7f1d1d',
  },

  // Floating button and overlay
  floatingButton: {
    position: 'absolute',
    right: 24,
    bottom: 24,
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: PRIMARY_COLOR,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 4},
    shadowOpacity: 0.2,
    shadowRadius: 8,
    elevation: 8,
  },
  floatingButtonMobile: {
    right: 16,
    bottom: 16,
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  floatingButtonText: {
    fontSize: 20,
  },
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
    zIndex: 200,
  },
  infoCard: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    maxWidth: 400,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 8},
    shadowOpacity: 0.2,
    shadowRadius: 16,
    elevation: 16,
  },
  infoTitle: {
    color: '#111827',
    marginBottom: 12,
  },
  infoText: {
    color: '#4b5563',
    marginBottom: 16,
    lineHeight: 22,
  },
  codeBlock: {
    backgroundColor: '#1f2937',
    padding: 16,
    borderRadius: 8,
    marginBottom: 12,
  },
  codeText: {
    fontFamily: 'Courier, monospace',
    color: '#a5f3fc',
    fontSize: 14,
  },
  infoHint: {
    color: '#9ca3af',
    textAlign: 'center',
  },
});

export default StorybookScreen;
